import DetailsLayout from "~/lib/components/layout/DetailsLayout";
import React from "react";
import {
  IArticleProps,
  IHelpCentreTopics,
  ISectionProps,
  ISubsectionProps,
} from "~/lib/types/article.type";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import { List } from "../../ui/List";
import { transformPathKebabCase } from "~/lib/utils/helper/transformPath";
import cn from "classnames";
import sampleSize from "lodash/sampleSize";
import { runTimeConfig } from "~/lib/utils/runtime-config";
import RecentActivities from "../home/RecentActivities";
import { useIsMobileUser } from "~/lib/utils/store/useIsMobileUser";
import { useAuth } from "~/lib/utils/hooks/useAuth";
import { useLocale } from "~/i18n/i18n.client";

interface IPageProps {
  articles?: IArticleProps[];
  sectionProps?: ISectionProps;
  selectedSubsection?: ISubsectionProps;
}

const getArticleLink = ({ article }: { article: IArticleProps }) => {
  return `/article/${transformPathKebabCase(article.title, `${article.id}/`)}`;
};

const SectionLayout = ({ articles = [], selectedSubsection }: IPageProps) => {
  const { t } = useTranslation();
  const { isAuthorized } = useAuth();
  const { isMobileUser } = useIsMobileUser();
  const isFuelingTopic =
    selectedSubsection?.value === IHelpCentreTopics.FUELLING;

  const renderArticles = () => {
    const commonIssuesArticles = articles.filter((article) =>
      article.label_names.includes("top_issue")
    );

    // we will get random 3 articles from common issues articles
    const highlightCommonIssuesArticles = sampleSize(commonIssuesArticles, 3);

    const otherArticles = [
      ...articles.filter(
        (article) => !article.label_names.includes("top_issue")
      ),
      ...commonIssuesArticles.filter(
        (article) => !highlightCommonIssuesArticles.includes(article)
      ),
    ];

    return (
      <div
        className={cn(
          !!articles.length || (isAuthorized && isFuelingTopic)
            ? "md:p-10 md:border-gray-500 md:border-[1px] md:rounded-xl md:flex md:flex-col md:gap-14"
            : ""
        )}
      >
        {isAuthorized &&
          isFuelingTopic &&
          runTimeConfig.HELP_CENTRE.TRANSACTION_ENABLED && (
            <div className="flex flex-col gap-4 md:p-0 p-5 pt-0 mb-5 md:mb-0 md:rounded-xl border-b-8 border-gray-500 md:border-0">
              <RecentActivities
                isVisibleTab={false}
                pricePosition={isMobileUser ? "bottom" : "right"}
              />
            </div>
          )}

        {!!highlightCommonIssuesArticles.length && (
          <div className="p-5 pt-0 md:p-0">
            <div className="mb-5 font-title text-black font-semibold text-xl">
              {t("commonIssues")}
            </div>
            <List
              isLoading={false}
              data={highlightCommonIssuesArticles.map((article) => ({
                label: article.title,
                value: getArticleLink({ article }),
              }))}
            />
          </div>
        )}
        {!!otherArticles.length && (
          <div
            className={cn(
              "p-5 md:p-0",
              commonIssuesArticles.length > 0
                ? "border-t-8 border-gray-500 md:border-0"
                : "pt-0"
            )}
          >
            <div className="mb-5 font-title text-black font-semibold text-xl">
              {t("otherArticles")}
            </div>
            <List
              isLoading={false}
              data={otherArticles.map((article) => ({
                label: article.title,
                value: getArticleLink({ article }),
              }))}
              numOfVisibleArticle={8}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="p-0 mx-auto w-full 2xl:px-[128px] md:px-[50px] xl:px-[100px] max-w-screen-md md:max-w-8xl md:p-5">
        <DetailsLayout selectedSubsection={selectedSubsection}>
          {renderArticles()}
        </DetailsLayout>
      </div>
    </>
  );
};

export default SectionLayout;
